/**
 * `maintenance` endpoint wrappers.
 *
 * Field shapes are derived from `serializeMaintenanceRequest` /
 * `serializeMaintenanceRequestRow` (refactor/backend/src/modules/maintenance/serializers.ts)
 * — see src/types/maintenance.ts for the full derivation notes. Route paths
 * and ordering are verified against refactor/backend/src/modules/maintenance/routes.ts.
 *
 * Permissions are unusual for this module: plain `IsAuthenticated` on the CRUD
 * routes (a tenant may PATCH/DELETE their OWN request), and only `assign`/
 * `complete` require a manager role. See the route file's top comment.
 *
 * `images/` has one wrapper — `uploadMaintenanceImage` — for the multipart
 * create; the images a caller needs to READ are already nested in
 * `MaintenanceRequest.images`, so no list/get wrapper exists for this path.
 */
import { apiClient, uploadFile } from '../client';
import { MAINTENANCE, MAINTENANCE_IMAGES } from './paths';
import type { Paginated } from '../types';
import type {
  AssignMaintenanceRequestRequest,
  CompleteMaintenanceRequestRequest,
  MaintenanceImage,
  MaintenanceRequest,
  MaintenanceRequestCreateRequest,
  MaintenanceRequestPatchRequest,
  MaintenanceRequestRow,
} from '../../types/maintenance';

/** `GET maintenance/` — paginated, scoped to the caller; `status`/`priority` are server-side filters. */
export async function listMaintenanceRequests(params?: {
  page?: number | undefined;
  status?: string | undefined;
  priority?: string | undefined;
}): Promise<Paginated<MaintenanceRequestRow>> {
  const response = await apiClient.get<Paginated<MaintenanceRequestRow>>(MAINTENANCE, { params });
  return response.data;
}

/** `GET maintenance/{id}/`. */
export async function getMaintenanceRequest(id: number): Promise<MaintenanceRequest> {
  const response = await apiClient.get<MaintenanceRequest>(`${MAINTENANCE}${id}/`);
  return response.data;
}

/**
 * `POST maintenance/`. `tenant` is forced server-side to the caller, whatever
 * their role — any authenticated caller may file a request against any unit.
 */
export async function createMaintenanceRequest(
  payload: MaintenanceRequestCreateRequest,
): Promise<MaintenanceRequest> {
  const response = await apiClient.post<MaintenanceRequest>(MAINTENANCE, payload);
  return response.data;
}

/** `PATCH maintenance/{id}/` — partial update; open to whoever can see the row, including its own tenant. */
export async function updateMaintenanceRequest(
  id: number,
  payload: MaintenanceRequestPatchRequest,
): Promise<MaintenanceRequest> {
  const response = await apiClient.patch<MaintenanceRequest>(`${MAINTENANCE}${id}/`, payload);
  return response.data;
}

/** `DELETE maintenance/{id}/` — same broad permission as PATCH. */
export async function deleteMaintenanceRequest(id: number): Promise<void> {
  await apiClient.delete(`${MAINTENANCE}${id}/`);
}

/** `POST maintenance/{id}/assign/` — manager roles only (LANDLORD/AGENT/SUPERAGENT). */
export async function assignMaintenanceRequest(
  id: number,
  payload: AssignMaintenanceRequestRequest,
): Promise<MaintenanceRequest> {
  const response = await apiClient.post<MaintenanceRequest>(`${MAINTENANCE}${id}/assign/`, payload);
  return response.data;
}

/** `POST maintenance/{id}/complete/` — manager roles only (LANDLORD/AGENT/SUPERAGENT). */
export async function completeMaintenanceRequest(
  id: number,
  payload: CompleteMaintenanceRequestRequest,
): Promise<MaintenanceRequest> {
  const response = await apiClient.post<MaintenanceRequest>(`${MAINTENANCE}${id}/complete/`, payload);
  return response.data;
}

/**
 * `POST maintenance/images/` — same broad permission as the request itself
 * (a tenant may upload images for their own request); requires no whitelist
 * beyond size (5MB, enforced server-side).
 */
export async function uploadMaintenanceImage(
  requestId: number,
  file: File,
  caption?: string,
): Promise<MaintenanceImage> {
  const formData = new FormData();
  formData.append('request', String(requestId));
  formData.append('image', file);
  if (caption !== undefined && caption !== '') formData.append('caption', caption);
  return uploadFile<MaintenanceImage>(MAINTENANCE_IMAGES, formData);
}
